import { useApolloClient } from "@apollo/client"
import { getApolloSandboxLink } from "@pathwright/utils/src/graphql/apolloOperation"
import { useMemo } from "react"
import { usePathwrightClientContext } from "../pathwright/PathwrightClient"
import PWAdminCmdKItem, { PWAdminCmdKItemProps } from "./PWAdminCmdKItem"

type Option = PWAdminCmdKItemProps & { option: { href: string } }

function PWAdminCmdKGraphQLOperations() {
  const { operations } = usePathwrightClientContext()
  const client = useApolloClient()

  const links: Option[] = useMemo(() => {
    const observedOperations = [...client.getObservableQueries().values()]

    // Separating active and inactive operations so active ones appear first and
    // we retain order of operations as provided by the context.
    const [activeOperationLinks, inactiveOperationLinks] = operations.reduce(
      ([accActive, accInactive], operation) => {
        const developmentLink = getApolloSandboxLink(operation, true)
        const operatingEnvLink = getApolloSandboxLink(operation, false)

        // Only adding if operation not already added that matches same href.
        // Operations sharing the same href are exact duplicates.
        if (
          ![...accActive, ...accInactive].some(
            (link) => link.option.href === developmentLink
          )
        ) {
          // If an observed operation matches this operation name, consider it active.
          // Observed queries are those used by currently mounted useQuery hooks.
          const isActive = observedOperations.some((observed) => {
            return observed.queryName === operation.operationName
          })

          const link: Option = {
            option: {
              label: operation.operationName,
              icon: "external-link",
              // Conditionally indicating when an operation is active.
              iconProps: operation.hasError
                ? { color: "red.400" }
                : isActive
                ? { color: "green.400" }
                : undefined,
              href: developmentLink,
              link: {
                href: operatingEnvLink,
                icon: "globe-fill"
              }
            }
            // Supply any Command.Item props here if needed.
          }

          // If there are other operations with the same name, differentiate the label
          // by the unique variables.
          if (
            operations.some(
              (op) =>
                op.operationName === operation.operationName && op !== operation
            )
          ) {
            if (Object.keys(operation.variables).length) {
              link.option.label = `${
                operation.operationName
              } - ${JSON.stringify(operation.variables)}`
            }
          }

          if (isActive) accActive.push(link)
          if (!isActive) accInactive.push(link)
        }

        return [accActive, accInactive]
      },
      [[], []] as [Option[], Option[]]
    )

    return [...activeOperationLinks, ...inactiveOperationLinks]
  }, [operations])

  return links.map((link) => (
    <PWAdminCmdKItem key={link.option.href} {...link} />
  ))
}

export default PWAdminCmdKGraphQLOperations
